import React, { useState, useEffect } from 'react';
import { getRosterForDate } from '../utils/rosterManager';
import TeamLogo from './TeamLogo';
import PlayerRow from './PlayerRow';

function TradeModal({ myTeam, myRoster, partnerTeam, league, leagueId, userId, currentDate, initialMyPlayers, initialPartnerPlayers, onClose, onSubmit }) {
  const [partnerRoster, setPartnerRoster] = useState(null);
  const [loadingPartner, setLoadingPartner] = useState(true);
  const [mySelected, setMySelected] = useState(() => new Set((initialMyPlayers || []).map(p => String(p.id))));
  const [partnerSelected, setPartnerSelected] = useState(() => new Set((initialPartnerPlayers || []).map(p => String(p.id))));

  useEffect(() => {
    let cancelled = false;
    setLoadingPartner(true);
    (async () => {
      try {
        const partnerTeamId = partnerTeam.id ?? partnerTeam.teamId;
        const roster = await getRosterForDate(partnerTeam.owner, leagueId, currentDate, league, partnerTeamId);
        if (!cancelled) setPartnerRoster(roster);
      } catch {
        if (!cancelled) setPartnerRoster(null);
      } finally {
        if (!cancelled) setLoadingPartner(false);
      }
    })();
    return () => { cancelled = true; };
  }, [partnerTeam, leagueId, currentDate, league]);

  const toggle = (setter) => (id) => {
    setter(prev => {
      const next = new Set(prev);
      const key = String(id);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const myPlayers = [...(myRoster?.players || []), ...(myRoster?.bench || [])];
  const partnerPlayers = [...(partnerRoster?.players || []), ...(partnerRoster?.bench || [])];
  const mySelectedPlayers = myPlayers.filter(p => mySelected.has(String(p.id)));
  const partnerSelectedPlayers = partnerPlayers.filter(p => partnerSelected.has(String(p.id)));
  const canReview = mySelected.size > 0 || partnerSelected.size > 0;

  // Wraps the shared PlayerRow (in `bare` mode, so it contributes no border/padding of its
  // own — see src/components/PlayerRow.js) with a selection checkbox and the modal's tighter
  // row spacing/divider. Keeps this list visually compact for the two-panel modal layout while
  // still reusing the shared avatar/name/meta rendering instead of a second local clone.
  const renderSelectableRow = (player, selected, onToggle) => (
    <button
      key={player.id}
      type="button"
      onClick={() => onToggle(player.id)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '8px 16px',
        width: '100%',
        border: 'none',
        borderBottom: '1px solid var(--border)',
        background: selected ? 'var(--primary-soft)' : 'transparent',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'background 0.12s',
        fontFamily: 'var(--font-family)',
      }}
    >
      <div style={{
        width: '18px', height: '18px', borderRadius: '5px', flexShrink: 0,
        border: `2px solid ${selected ? 'var(--primary)' : 'var(--border-strong)'}`,
        background: selected ? 'var(--primary)' : 'transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        transition: 'all 0.12s',
      }}>
        {selected && (
          <svg width="9" height="7" viewBox="0 0 9 7" fill="none">
            <path d="M1 3.5L3 5.5L8 1" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <PlayerRow player={player} bare />
      </div>
    </button>
  );

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', zIndex: 10000, display: 'flex', alignItems: 'stretch', justifyContent: 'center', padding: '24px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{ background: 'var(--card)', borderRadius: '20px', width: '100%', maxWidth: '860px', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: 'var(--shadow-xl)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', letterSpacing: '-0.022em', color: 'var(--foreground)' }}>
              Trade Proposal
            </h2>
            <p style={{ margin: '2px 0 0', fontSize: '14px', color: 'var(--muted)', letterSpacing: '-0.022em' }}>
              Tap players on each side to include in the trade
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ width: '32px', height: '32px', borderRadius: '50%', border: 'none', background: 'var(--surface-muted)', cursor: 'pointer', fontSize: '20px', color: 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        {/* Team column headers */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
          {[[myTeam, mySelected.size, 'Your roster'], [partnerTeam, partnerSelected.size, "Their roster"]].map(([team, count, label], i) => (
            <div key={i} style={{ padding: '12px 16px', borderRight: i === 0 ? '1px solid var(--border)' : 'none', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <TeamLogo team={team} size={32} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '15px', fontWeight: '600', letterSpacing: '-0.022em', color: 'var(--foreground)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {team.name}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--muted)', letterSpacing: '-0.022em' }}>{label}</div>
              </div>
              {count > 0 && (
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '22px', height: '22px', borderRadius: '9999px', background: 'var(--primary)', color: '#fff', fontSize: '12px', fontWeight: '700', padding: '0 6px', flexShrink: 0 }}>
                  {count}
                </span>
              )}
            </div>
          ))}
        </div>

        {/* Two-panel rosters with independent scroll */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {/* My roster */}
          <div style={{ overflowY: 'auto', borderRight: '1px solid var(--border)' }}>
            {myPlayers.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: 'var(--muted)', fontSize: '14px', letterSpacing: '-0.022em' }}>
                No players on your roster
              </div>
            ) : myPlayers.map(p => (
              renderSelectableRow(p, mySelected.has(String(p.id)), toggle(setMySelected))
            ))}
          </div>

          {/* Partner roster */}
          <div style={{ overflowY: 'auto' }}>
            {loadingPartner ? (
              <div style={{ padding: '32px', textAlign: 'center', color: 'var(--muted)', fontSize: '14px', letterSpacing: '-0.022em' }}>
                <div className="apple-spinner" style={{ margin: '0 auto 10px' }} />
                Loading roster…
              </div>
            ) : partnerPlayers.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: 'var(--muted)', fontSize: '14px', letterSpacing: '-0.022em' }}>
                No players available
              </div>
            ) : partnerPlayers.map(p => (
              renderSelectableRow(p, partnerSelected.has(String(p.id)), toggle(setPartnerSelected))
            ))}
          </div>
        </div>

        {/* Sticky footer */}
        <div style={{ borderTop: '1px solid var(--border)', padding: '14px 24px', background: 'var(--surface)', display: 'flex', alignItems: 'center', gap: '16px', flexShrink: 0, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <div style={{ fontSize: '11px', color: 'var(--muted)', letterSpacing: '0.04em', marginBottom: '3px', fontWeight: '600', textTransform: 'uppercase' }}>
              Trade summary
            </div>
            <div style={{ fontSize: '14px', letterSpacing: '-0.022em', color: 'var(--foreground)', lineHeight: 1.4 }}>
              {!canReview ? (
                <span style={{ color: 'var(--muted)' }}>Select players on both sides to begin</span>
              ) : (
                <>
                  <strong>You give:</strong>{' '}
                  {mySelectedPlayers.length ? mySelectedPlayers.map(p => p.name).join(', ') : '—'}
                  {' · '}
                  <strong>You receive:</strong>{' '}
                  {partnerSelectedPlayers.length ? partnerSelectedPlayers.map(p => p.name).join(', ') : '—'}
                </>
              )}
            </div>
          </div>
          <button onClick={onClose} className="btn btn-secondary btn-sm">
            Discard
          </button>
          <button
            onClick={() => onSubmit({ sending: mySelectedPlayers, receiving: partnerSelectedPlayers, partnerTeam })}
            disabled={!canReview}
            className="btn btn-primary btn-sm"
            style={{ opacity: canReview ? 1 : 0.4 }}
          >
            Review Trade →
          </button>
        </div>
      </div>
    </div>
  );
}

export default TradeModal;
